import type { MapTag, NodeMapSettingType } from "~/types/ui";
import type { NodeMapColorThreshold, NodeMapScenario } from "~/types/api";

export function findSensor(
  tag: MapTag,
  keys: string[],
): { key: string; value: string } | undefined {
  for (const key of keys) {
    const sensor = tag.sensors.find((s) => s.key === key);
    if (sensor?.current !== undefined) return { key, value: sensor.current };
  }
  return undefined;
}

export const ICON_COLOR_GOOD = "#64de64";
export const ICON_COLOR_AVG = "#fffc38";
export const ICON_COLOR_BAD = "#fa4d4d";
export const ICON_COLOR_NO_SIGNAL = "#8f8f8f";

export const DEFAULT_NODE_MAP_SETTINGS: NodeMapSettingType = {
  displayAttributes: [
    {
      nodeType: 0,
      attribute: { normalizedName: "RSSI", name: "rssi" },
    },
  ],
  slidervalues: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 24, 36, 48],
  showTypes: [0, 1, 2, 3, 4, 5, 6],
};

export const BUILT_IN_SCENARIOS: NodeMapScenario[] = [
  {
    name: "Rssi",
    description: "Signal testing",
    sensorKeys: ["rssi", "RSSI", "Rssi"],
    thresholds: [
      { thresholdValue: -120, color: ICON_COLOR_BAD },
      { thresholdValue: -115, color: ICON_COLOR_AVG },
      { thresholdValue: null, color: ICON_COLOR_GOOD },
    ],
  },
];

/** The part of a scenario the editor changes directly. Name and description are left
 * out: they only change through the name dialog, which saves at once. */
export type ScenarioContent = {
  sensorKeys: string[];
  displayAttributeKey: string | undefined;
  thresholds: NodeMapColorThreshold[];
};

export const EMPTY_SCENARIO_CONTENT: ScenarioContent = {
  sensorKeys: [],
  displayAttributeKey: undefined,
  thresholds: [],
};

export function scenarioContent(scenario: NodeMapScenario): ScenarioContent {
  return {
    sensorKeys: scenario.sensorKeys ?? [],
    displayAttributeKey: scenario.displayAttribute?.normalizedName || undefined,
    thresholds: scenario.thresholds ?? [],
  };
}

/** Lowercase 6-digit hex where possible, so "#FFF" and "#ffffff" from the color picker
 * don't count as a change. Anything else (named colors) is only lowercased. */
export function normalizeColor(color: string | undefined | null): string {
  const c = (color ?? "").trim().toLowerCase();
  const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/.exec(c);
  return short ? `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}` : c;
}

/** Whether two scenario contents would render the same map. Sensor keys are a set;
 * bands are ordered, since each band spans up to its value from the one before. */
export function isSameScenarioContent(
  a: ScenarioContent,
  b: ScenarioContent,
): boolean {
  const keysA = new Set(a.sensorKeys);
  const keysB = new Set(b.sensorKeys);
  if (keysA.size !== keysB.size || [...keysA].some((k) => !keysB.has(k)))
    return false;

  if ((a.displayAttributeKey ?? "") !== (b.displayAttributeKey ?? ""))
    return false;

  return (
    a.thresholds.length === b.thresholds.length &&
    a.thresholds.every((t, i) => {
      const other = b.thresholds[i];
      return (
        (t.thresholdValue ?? null) === (other.thresholdValue ?? null) &&
        normalizeColor(t.color) === normalizeColor(other.color) &&
        (t.severity ?? "normal") === (other.severity ?? "normal")
      );
    })
  );
}

/** Scenario names are unique per company, compared the way the server does. */
export function normalizeScenarioName(name: string): string {
  return name.trim().toLowerCase();
}

/** `base`, or `base (2)`, `base (3)` … if taken, kept within `maxLength` by shortening
 * the base. Pre-fills the "Save as new" dialog with a name that is valid as it stands. */
export function uniqueScenarioName(
  base: string,
  takenNames: string[],
  maxLength = 100,
): string {
  const taken = new Set(takenNames.map(normalizeScenarioName));
  const fit = (name: string, suffix = "") =>
    `${name.trim().slice(0, maxLength - suffix.length).trimEnd()}${suffix}`;

  if (!taken.has(normalizeScenarioName(fit(base)))) return fit(base);
  for (let n = 2; ; n++) {
    const candidate = fit(base, ` (${n})`);
    if (!taken.has(normalizeScenarioName(candidate))) return candidate;
  }
}
